import FileUploader from "./FileUploader";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Dialog } from "@mui/material";
import Authentication from "../modules/authentication";
import DataTable from "./DataTable";

const gApiClient = Authentication.getAPIClient();

/**
 * File importer dialog component - pop-up dialog that allows the user to upload a file,
 * then calls an API to upload that file (while displaying a progress dialog), and finally
 * showing the response.
 *
 * @param show Should the dialog be shown
 * @param onCancelled When the user cancels the dialog without choosing a file
 * @param onFinished a callback function, called when the file was imported successfully.
 * @param title Title of the dialog
 * @param uploadAPIPath The API path to call for importing the file (e.g. 'import_corporate_actions')
 * @param confirmButtonText Text of the confirm/OK button of the dialog
 * @param cancelButtonText Text of the cancel button of the dialog
 * @param closeButtonText Text of the close button of the dialog
 * @param importsText Text that appears above the table showing the list of successful records imported
 * @param uploadAgainButtonText Text of the 'Upload Another File' button of the dialog
 * @param importsTableColumns list of columns - used when displaying the imported items (e.g. CAs) - see `DataTable` component
 * @param importsTableOptions table options - used when displaying the imported items (e.g. CAs) - see `DataTable` component
 * @param transformChanges (optional) callback function that receives a single result row from the import, and transforms
 *      it - e.g. adds an index property to an IA result
 * @param changesKey (optional) key of the imported records list in the API response (e.g. 'securities')
 * @param successText (optional) text shown when the import finished without errors
 * @param confirmation (optional) lets the user re-upload the same file with extra params, when the API
 *      rejects the file pending confirmation. An object of:
 *      - key: key of the list of rows that need confirmation in the API response (e.g. 'disabled_securities')
 *      - title: title of the table of rows that need confirmation
 *      - text: warning text shown above those rows
 *      - columns: columns of the rows table (used for both the rows needing confirmation and the confirmed rows)
 *      - renderRows: (optional) function that receives a list of rows and renders them - replaces the default
 *          table (title + columns), for both the rows needing confirmation and the confirmed rows
 *      - actions: list of confirm buttons, each an object of: { text: button text, params: extra query params sent
 *          with the re-upload (e.g. { enable_disabled_securities: true }), color: (optional, default="primary")
 *          MUI palette color of the button }
 *      - confirmedKey: key of the list of rows that were changed by the re-upload in the API response
 *      - confirmedText: text that appears above the table of confirmed rows
 */
export default function FileImporterDialog({
    show,
    onCancelled,
    onFinished,
    title,
    uploadAPIPath,
    importsTableColumns,
    importsTableOptions = {},
    transformChanges = (c) => c,
    confirmButtonText = "Upload",
    cancelButtonText = "Cancel",
    closeButtonText = "Close",
    importsText = "Successful Imports",
    uploadAgainButtonText = "Upload Another File",
    changesKey = "changes",
    successText = "File imported successfully",
    confirmation = null,
}) {
    const [chosenFilename, setChosenFilename] = useState(null);
    const [chosenFileBody, setChosenFileBody] = useState(null);
    const [importing, setImporting] = useState(false);
    const [finished, setFinished] = useState(false);
    const [errors, setErrors] = useState([]);
    const [changes, setChanges] = useState([]);
    const [succeeded, setSucceeded] = useState(false);
    const [rowsToConfirm, setRowsToConfirm] = useState([]);
    const [confirmedRows, setConfirmedRows] = useState([]);

    const resetResults = () => {
        setErrors([]);
        setChanges([]);
        setSucceeded(false);
        setRowsToConfirm([]);
        setConfirmedRows([]);
    };

    const resetDialog = useCallback(() => {
        setChosenFileBody(null);
        setChosenFilename(null);
        setImporting(false);
        setFinished(false);
        resetResults();
    }, []);

    useEffect(() => {
        if (show) {
            // Dialog was re-shown - Reset everything
            resetDialog();
        }
    }, [resetDialog, show]);

    const onCurrentFileChosen = (filename, fileBody) => {
        setChosenFilename(filename);
        setChosenFileBody(fileBody);
    };

    /** Renders the rows needing confirmation / confirmed rows */
    const renderConfirmationRows = (rows, title) =>
        confirmation.renderRows ? (
            confirmation.renderRows(rows)
        ) : (
            <div className="overflow-y-hidden">
                <DataTable
                    title={title}
                    data={rows}
                    columns={confirmation.columns}
                />
            </div>
        );

    /** Uploads the file - `confirmParams` are the params of the confirm button pressed (null => not confirmed) */
    const onUploadFile = async (confirmParams = null) => {
        const confirmed = confirmParams !== null;
        resetResults();
        setImporting(true);

        // Call the API for uploading the file
        const fileExtension = chosenFilename
            .substring(chosenFilename.lastIndexOf(".") + 1)
            .toLowerCase();

        try {
            const response = await gApiClient.callApi(uploadAPIPath, {
                method: "POST",
                params: {
                    file_type: ["xls", "xlsx"].includes(fileExtension)
                        ? "xls"
                        : "csv",
                    ...confirmParams,
                },
                queryParams: true,
                body: chosenFileBody,
            });
            const responseData = response.data;

            if (responseData.errors) {
                setErrors(responseData.errors);
            }
            const responseChanges = responseData[changesKey];
            if (responseChanges) {
                setChanges(responseChanges.map((c) => transformChanges(c)));
            }
            if (confirmation) {
                // The confirmed upload's response may still list the rows that needed confirmation
                if (!confirmed) {
                    setRowsToConfirm(responseData[confirmation.key] ?? []);
                }
                setConfirmedRows(responseData[confirmation.confirmedKey] ?? []);
            }
            // Not all import APIs return a 'success' flag - fall back to checking for errors
            setSucceeded(responseData.success ?? !responseData.errors?.length);
        } catch (exc) {
            console.error(exc);
            setErrors([`Network Error: ${exc.message}`]);
        }

        setImporting(false);
        setFinished(true);
    };

    return (
        <Dialog
            open={show}
            onClose={() => {
                if (!importing) onCancelled();
            }}
            maxWidth={finished ? "md" : "sm"}
            fullWidth={true}
        >
            <div className="p-5 flex flex-col items-center">
                <div className="text-center mb-4 text-3xl">{title}</div>
                {importing && (
                    <div className="flex flex-col p-10 text-center items-center">
                        <CircularProgress color="secondary" size={55} />
                        <div className="mt-5 text-xl">Importing...</div>
                    </div>
                )}
                {!importing && !finished && (
                    <>
                        <FileUploader onFileChosen={onCurrentFileChosen} />
                        <div className="flex flex-row justify-center pt-4 gap-4">
                            <Button
                                variant="contained"
                                disabled={!chosenFilename}
                                onClick={() => onUploadFile()}
                            >
                                {confirmButtonText}
                            </Button>
                            <Button variant="outlined" onClick={onCancelled}>
                                {cancelButtonText}
                            </Button>
                        </div>
                    </>
                )}
                {finished && succeeded && errors.length === 0 && (
                    <Alert severity="success">
                        {successText}
                        {changes.length > 0 &&
                            ` (${changes.length} ${changes.length === 1 ? "record" : "records"
                            })`}
                    </Alert>
                    // <div className="mt-6 flex flex-row items-center gap-2 text-xl text-green-600">
                    //     <CheckCircle />
                    //     <div>
                    //         {successText}
                    //         {changes.length > 0 &&
                    //             ` (${changes.length} ${
                    //                 changes.length === 1 ? "record" : "records"
                    //             })`}
                    //     </div>
                    // </div>
                )}
                {errors.length > 0 && (
                    <div className="mb-10 mt-10">
                        <div className="font-bold text-red-500">Errors:</div>
                        <div className="max-h-[200px] overflow-x-hidden">
                            {errors.map((e) => (
                                <div key={e}>• {e}</div>
                            ))}
                        </div>
                    </div>
                )}
                {rowsToConfirm.length > 0 && (
                    <div className="mb-10 mt-10 max-w-full">
                        <Alert severity="warning">{confirmation.text}</Alert>
                        <div className="font-bold mt-10 mb-10">
                            {confirmation.title && `${confirmation.title}:`}
                        </div>
                        {renderConfirmationRows(
                            rowsToConfirm,
                            confirmation.title,
                        )}
                    </div>
                )}
                {confirmedRows.length > 0 && (
                    <div className="mb-10 mt-10 max-w-full">
                        <div className="font-bold mb-10">
                            {confirmation.confirmedText}:
                        </div>
                        {renderConfirmationRows(
                            confirmedRows,
                            confirmation.confirmedText,
                        )}
                    </div>
                )}
                {changes.length > 0 && (
                    <div className="mb-10 mt-10 max-w-full">
                        <div className="font-bold mb-10">{importsText}:</div>
                        <div className="overflow-y-hidden">
                            <DataTable
                                title={importsText}
                                data={changes}
                                columns={importsTableColumns}
                                options={importsTableOptions}
                            />
                        </div>
                    </div>
                )}
                {finished && (
                    <>
                        <div className="flex flex-row justify-center pt-4 gap-4">
                            {rowsToConfirm.length > 0 &&
                                confirmation.actions.map((action) => (
                                    <Button
                                        key={action.text}
                                        variant="contained"
                                        color={action.color || "primary"}
                                        onClick={() =>
                                            onUploadFile(action.params)
                                        }
                                    >
                                        {action.text}
                                    </Button>
                                ))}
                            <Button variant="contained" onClick={resetDialog}>
                                {uploadAgainButtonText}
                            </Button>
                            <Button variant="outlined" onClick={onFinished}>
                                {closeButtonText}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Dialog >
    );
}
